import { FC, ReactNode, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { TaskStatus } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { AnalysisType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { CallIntelConstant, CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  CheckIcon,
  Chip,
  FeedbackBadIcon,
  FeedbackIcon,
  IconProps,
  statusSwitcherVariantsColor,
  Text,
  XIcon,
} from '@frontend/design-system';
import { AiOutcomeIcon } from '../components/ai-outcome-icon';
import { CallIntelChip } from '../components/chip/call-intel-chip';
import { GroupOption } from '../components/menu/grouped-check-list-dropdown-menu';
import { Thumb } from '../components/thumb';
import { FIELD_MAPPING } from '../utils/constant';
import { useCallIntelDefaultsShallowStore } from './use-call-intel-defaults';
import { useHasCallIntelAclAccess } from './use-has-call-intel-acl-access';

type Variant = 'array' | 'groupedArray' | 'filterRecord' | 'record';
type Options = { id: string; label: string }[];
export type RecordOptions = Record<string, ReactNode | string>;
type OptionsReturnType<V extends Variant> = V extends 'array'
  ? Options
  : V extends 'groupedArray'
  ? GroupOption[]
  : RecordOptions;

export type OptionType =
  | 'aiCallOutcome'
  | 'appointmentTypes'
  | 'callDirection'
  | 'categories'
  | 'contactType'
  | 'feedbackIssueTypes'
  | 'officeHours'
  | 'schedulingOutcome'
  | 'schedulingOutcomeV2'
  | 'schedulingOpportunity'
  | 'sentiment'
  | 'serviceQualityFlag'
  | 'taskStatus'
  | 'taskType';

export type OptionAdditionalProps = {
  excludeOptions?: string[];
  extraAppointmentTypes?: CallIntelTypes.AppointmentTypeEnum[];
  includeEmptyOption?: boolean;
  showDisabledChipForRecoveredOpportunity?: boolean;
  withoutEmoji?: boolean;
  withoutLabel?: boolean;
};

export const verticalMapping: Partial<Record<Vertical, readonly CallIntelTypes.AppointmentTypeEnum[]>> = {
  [Vertical.DENTAL]: CallIntelConstant.APPOINTMENT_TYPE_DENTAL,
  [Vertical.VET]: CallIntelConstant.APPOINTMENT_TYPE_VET,
  [Vertical.OPTOMETRY]: CallIntelConstant.APPOINTMENT_TYPE_OPTO,
};

const renderOpportunityOption = (
  mainText: string,
  subText: string,
  IconComponent: FC<IconProps>,
  variant: 'primary' | 'neutral'
) => (
  <>
    <div css={styles.chipLabel}>
      <Chip css={styles.chip} variant={variant}>
        <IconComponent size={16} />
      </Chip>
      <Text>{mainText}</Text>
    </div>
    <Text color='subdued' size='small'>
      {subText}
    </Text>
  </>
);

export const useOptionsProvider = () => {
  const { t } = useTranslation('analytics');
  const { availableCategories, chipVariants, dataLabels } = useCallIntelDefaultsShallowStore(
    'availableCategories',
    'chipVariants',
    'dataLabels'
  );
  const { patientCapitalized: patientCapitalizedToken } = useTranslationTokens();
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const isServiceQualityEnabled = getFeatureFlagValue('enable-service-quality');
  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);

  const feedbackIssueTypesOptions = useCallback(
    <V extends Variant>(variant: V): OptionsReturnType<V> => {
      const options: Options = [
        { id: AnalysisType.ANALYSIS_TYPE_SUMMARY, label: t('Summary') },
        { id: AnalysisType.ANALYSIS_TYPE_TRANSCRIPT, label: t('Transcript') },
        { id: AnalysisType.ANALYSIS_TYPE_TASKS, label: t('Tasks') },
        { id: AnalysisType.ANALYSIS_TYPE_CONTACT_TYPE, label: t('Contact Type') },
        { id: AnalysisType.ANALYSIS_TYPE_SENTIMENT, label: t('Customer Sentiment') },
        ...(isServiceQualityEnabled && hasServiceQualityAccess
          ? [{ id: AnalysisType.ANALYSIS_TYPE_SERVICE_QUALITY_V2, label: t('Service Quality') }]
          : []),
        { id: AnalysisType.ANALYSIS_TYPE_SCHEDULING_OUTCOME_V2, label: t('Scheduling Outcome') },
        { id: AnalysisType.ANALYSIS_TYPE_CATEGORY, label: t('Category') },
        { id: AnalysisType.ANALYSIS_TYPE_APPOINTMENT_TYPE, label: t('Appointment Type') },
        { id: AnalysisType.ANALYSIS_TYPE_OTHER, label: t('Other') },
      ];

      if (variant === 'array') {
        return options as OptionsReturnType<V>;
      }

      return options.reduce((acc, { id, label }) => {
        acc[id] = label;
        return acc;
      }, {} as RecordOptions) as OptionsReturnType<V>;
    },
    [t, isServiceQualityEnabled, hasServiceQualityAccess]
  );

  const aiCallOutcomeGroupedOptions = useCallback(() => {
    return [
      {
        children: [],
        label: (
          <div css={[styles.chipLabel, styles.aiIcon]}>
            <AiOutcomeIcon outcome={AIOutcome.AI_OUTCOME_ROUTE_MISSED} />
            <Text>{t('Needs attention')}</Text>
          </div>
        ),
        value: AIOutcome.AI_OUTCOME_ROUTE_MISSED,
      },
      {
        children: [
          {
            label: t('Unable to answer'),
            value: AIOutcome.AI_OUTCOME_FATAL,
          },
        ],
        label: (
          <div css={[styles.chipLabel, styles.aiIcon]}>
            <AiOutcomeIcon outcome={AIOutcome.AI_OUTCOME_FATAL} />
            <Text>{t('Error')}</Text>
          </div>
        ),
        value: AIOutcome.AI_OUTCOME_FATAL,
      },
      {
        children: [],
        label: (
          <div css={[styles.chipLabel, styles.aiIcon]}>
            <AiOutcomeIcon outcome={AIOutcome.AI_OUTCOME_INCOMPLETE} />
            <Text>{t('Incomplete')}</Text>
          </div>
        ),
        value: AIOutcome.AI_OUTCOME_INCOMPLETE,
      },
      {
        children: [
          {
            label: t('Office assisted'),
            value: AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED,
          },
          {
            label: t('Continued over text'),
            value: AIOutcome.AI_OUTCOME_TEXT_HANDOFF,
          },
        ],
        label: (
          <div css={[styles.chipLabel, styles.aiIcon]}>
            <AiOutcomeIcon outcome={AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED} />
            <Text>{t('Call Transferred')}</Text>
          </div>
        ),
        value: AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED,
      },
      {
        children: [],
        label: (
          <div css={[styles.chipLabel, styles.aiIcon]}>
            <AiOutcomeIcon outcome={AIOutcome.AI_OUTCOME_END_CALL} />
            <Text>{t('Successfully assisted patient')}</Text>
          </div>
        ),
        value: AIOutcome.AI_OUTCOME_END_CALL,
      },
    ];
  }, [t]);

  const getAppointmentTypesForVertical = useCallback((vertical: Vertical): CallIntelTypes.AppointmentTypeEnum[] => {
    // If a match is found in the mapping, return the specific appointment types
    if (vertical in verticalMapping) {
      return [...(verticalMapping[vertical] ?? [])].sort();
    }

    // If the vertical is NOT Dental, Vet, or Optometry, return Generic Appointment Types
    const allSpecificAppointments = new Set<CallIntelTypes.AppointmentTypeEnum>([
      ...CallIntelConstant.APPOINTMENT_TYPE_DENTAL,
      ...CallIntelConstant.APPOINTMENT_TYPE_VET,
      ...CallIntelConstant.APPOINTMENT_TYPE_OPTO,
    ]);

    return Object.values(CallIntelTypes.AppointmentTypeEnum)
      .filter((type) => !allSpecificAppointments.has(type))
      .sort();
  }, []);

  const appointmentTypesOptions = useCallback(
    <V extends Variant>(
      variant: V,
      additionalProps?: { extraAppointmentTypes?: CallIntelTypes.AppointmentTypeEnum[] }
    ): OptionsReturnType<V> => {
      let verticals = getAppointmentTypesForVertical(lastUsedVertical as Vertical);
      if (additionalProps?.extraAppointmentTypes?.length) {
        verticals = [...new Set([...verticals, ...additionalProps.extraAppointmentTypes])].sort();
      }
      const options = verticals.filter((type) => type !== CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN);

      if (variant === 'record') {
        return options.reduce((acc, type) => {
          acc[type] = (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={type}
              label={dataLabels.appointmentTypes?.[type] || type}
            />
          );
          return acc;
        }, {} as RecordOptions) as OptionsReturnType<V>;
      }

      return options.map((type) => ({
        id: type,
        label: dataLabels.appointmentTypes?.[type] || type,
      })) as OptionsReturnType<V>;
    },
    [chipVariants, dataLabels, getAppointmentTypesForVertical, lastUsedVertical]
  );

  const callDirectionOptions: RecordOptions = useMemo(() => {
    return {
      [CallIntelTypes.CallDirectionEnum.DIRECTION_INBOUND]: t('Inbound Calls'),
      [CallIntelTypes.CallDirectionEnum.DIRECTION_OUTBOUND]: t('Outbound Calls'),
    };
  }, [t]);

  const categoryOptions = useCallback(
    <V extends Variant>(variant: V): OptionsReturnType<V> => {
      const options = availableCategories.filter((type) => type !== CallIntelTypes.CategoryEnum.CATEGORY_UNKNOWN);

      if (variant === 'record') {
        return options.reduce((acc, type) => {
          acc[type] = (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={type}
              label={dataLabels.categories?.[type] || type}
            />
          );
          return acc;
        }, {} as RecordOptions) as OptionsReturnType<V>;
      }

      return options.map((type) => ({
        id: type,
        label: dataLabels.categories?.[type] || type,
      })) as OptionsReturnType<V>;
    },
    [availableCategories, chipVariants, dataLabels]
  );

  const contactTypeOptions: RecordOptions = useMemo(() => {
    return {
      [CallIntelTypes.ContactTypeEnum.CONTACT_NEW_PATIENT]: (
        <Chip css={styles.chip} variant='warn'>
          {t('New {{patientCapitalizedToken}}', { patientCapitalizedToken })}
        </Chip>
      ),
      [CallIntelTypes.ContactTypeEnum.CONTACT_EXISTING_PATIENT]: (
        <Chip css={styles.chip} variant='primary'>
          {t('Existing {{patientCapitalizedToken}}', { patientCapitalizedToken })}
        </Chip>
      ),
      [CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT]: (
        <Chip css={styles.chip} variant='neutral'>
          {t('Not a {{patientCapitalizedToken}}', { patientCapitalizedToken })}
        </Chip>
      ),
    };
  }, [t, patientCapitalizedToken]);

  const officeHoursOptions = useCallback(
    <V extends Variant>(variant: V): OptionsReturnType<V> => {
      const options = [
        { id: CallIntelConstant.ALL_OFFICE_HOURS_ID, label: t('All Hours') },
        { id: CallIntelTypes.OfficeHoursEnum.OFFICE_HOURS_OPEN, label: t('Open Hours') },
        { id: CallIntelTypes.OfficeHoursEnum.OFFICE_HOURS_CLOSED, label: t('Closed Hours') },
      ];

      if (variant === 'array') {
        return options as OptionsReturnType<V>;
      }

      return options.reduce((acc, { id, label }) => {
        acc[id] = label;
        return acc;
      }, {} as RecordOptions) as OptionsReturnType<V>;
    },
    [t]
  );

  const schedulingOutcomesOptions = useCallback(
    (variant: Variant, additionalProps?: OptionAdditionalProps): RecordOptions => {
      const { withoutLabel, includeEmptyOption } = additionalProps ?? {};
      //This could be simplified more but it has been done intentionally as in future filterrecords will look differently as normal records
      if (variant === 'filterRecord') {
        return {
          [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: (
            <div css={styles.chipLabel}>
              <Chip css={styles.chip} variant='success'>
                <CheckIcon size={16} />
              </Chip>
              {!withoutLabel && <Text>{t('Scheduled Appointment')}</Text>}
            </div>
          ),
          [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: (
            <div css={styles.chipLabel}>
              <Chip css={styles.chip} variant='warn'>
                <XIcon size={16} />
              </Chip>
              {!withoutLabel && <Text>{t('Unscheduled Appointment')}</Text>}
            </div>
          ),
        };
      }

      return {
        [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: (
          <div css={styles.chipLabel}>
            <Chip css={styles.chip} variant='success'>
              <CheckIcon size={16} />
            </Chip>
            {!withoutLabel && <Text>{t('Scheduled')}</Text>}
          </div>
        ),
        [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: (
          <div css={styles.chipLabel}>
            <Chip css={styles.chip} variant='warn'>
              <XIcon size={16} />
            </Chip>
            {!withoutLabel && <Text>{t('Unscheduled')}</Text>}
          </div>
        ),
        ...(includeEmptyOption && {
          none: FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME]?.emptyValue,
        }),
      };
    },
    [t]
  );

  const schedulingOpportunityOptions = useCallback(
    (variant: Variant, additionalProps?: OptionAdditionalProps): RecordOptions => {
      const { withoutLabel } = additionalProps ?? {};

      if (variant === 'filterRecord') {
        return {
          [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED]: renderOpportunityOption(
            t('Identified Scheduling Opportunity'),
            t('There was potential to schedule an appointment during the call.'),
            FeedbackIcon,
            'primary'
          ),
          [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED]: renderOpportunityOption(
            t('No Scheduling Opportunity'),
            t("There wasn't potential to schedule an appointment during the call."),
            FeedbackBadIcon,
            'neutral'
          ),
        };
      }

      return {
        [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED]: (
          <div css={styles.chipLabel}>
            <Thumb up />
            {!withoutLabel && <Text>{t('Opportunity')}</Text>}
          </div>
        ),
        [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED]: (
          <div css={styles.chipLabel}>
            <Thumb up={false} />
            {!withoutLabel && <Text>{t('No Opportunity')}</Text>}
          </div>
        ),
      };
    },
    [t]
  );

  const schedulingOutcomeV2Options = useCallback(
    <V extends Variant>(variant: V, additionalProps?: OptionAdditionalProps): OptionsReturnType<V> => {
      const allOptions: CallIntelTypes.SchedulingOutcomeV2Enum[] = [
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL,
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED,
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL,
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY,
      ];

      const { excludeOptions, showDisabledChipForRecoveredOpportunity } = additionalProps ?? {};

      const optionsToExclude = new Set(Array.isArray(excludeOptions) ? excludeOptions : []);
      const filteredOptions = allOptions.filter((option) => !optionsToExclude.has(option));

      if (variant === 'array') {
        return filteredOptions.map((type) => ({
          id: type,
          label: dataLabels.schedulingOutcomeV2?.[type] || type,
        })) as OptionsReturnType<V>;
      }

      if (showDisabledChipForRecoveredOpportunity && variant === 'filterRecord') {
        return {
          [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: (
            <div css={styles.schedulingContentWrapper}>
              <div css={styles.unscheduledChipWrapper}>
                <CallIntelChip
                  opacity={0.5}
                  as='schedulingOutcome'
                  chipVariants={chipVariants}
                  dataKey={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
                  label={
                    dataLabels.schedulingOutcomeV2?.[
                      CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED
                    ] ?? CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED
                  }
                  key={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
                  strikeThrough
                  strikeThroughConfig={{ color: theme.colors.warning80 }}
                />

                <Icon name='arrow-right-small' />
              </div>

              <CallIntelChip
                as='schedulingOutcome'
                chipVariants={chipVariants}
                dataKey={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL}
                label={
                  dataLabels.schedulingOutcomeV2?.[
                    CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
                  ] ?? CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
                }
                key={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL}
              />
            </div>
          ),
        } as unknown as OptionsReturnType<V>; // TypeScript workaround for specific case
      }

      return filteredOptions.reduce((acc, type) => {
        acc[type] = (
          <CallIntelChip
            as='chip'
            chipVariants={chipVariants}
            css={styles.chip}
            dataKey={type}
            label={dataLabels.schedulingOutcomeV2?.[type] || type}
          />
        );
        return acc;
      }, {} as RecordOptions) as OptionsReturnType<V>;
    },
    [chipVariants, dataLabels.schedulingOutcomeV2]
  );

  const sentimentOptions = useCallback(
    <V extends Variant>(variant: V, additionalProps?: OptionAdditionalProps): OptionsReturnType<V> => {
      const { withoutEmoji } = additionalProps ?? {};

      const options: CallIntelTypes.SentimentEnum[] = [
        CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE,
        CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL,
        CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE,
      ];

      if (variant === 'record') {
        return options.reduce((acc, type) => {
          acc[type] = (
            <CallIntelChip
              as='chip'
              chipVariants={chipVariants}
              css={styles.chip}
              dataKey={type}
              label={dataLabels.sentiments?.[type] || type}
            />
          );
          return acc;
        }, {} as RecordOptions) as OptionsReturnType<V>;
      }

      return options.map((type) => ({
        id: type,
        label: withoutEmoji ? dataLabels.sentiments?.[type] || type : dataLabels.sentimentsWithEmoji?.[type] || type,
      })) as OptionsReturnType<V>;
    },
    [chipVariants, dataLabels]
  );

  const serviceQualityFlagOptions = useCallback(
    <V extends Variant>(variant: V): OptionsReturnType<V> => {
      const options: CallIntelTypes.ServiceQualityFlagEnum[] = [
        CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT,
        CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER,
        CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION,
      ];

      if (variant === 'record') {
        return options.reduce((acc, type) => {
          acc[type] = (
            <CallIntelChip
              as='serviceQualityFlag'
              chipVariants={chipVariants}
              css={styles.chip}
              dataKey={type}
              label={dataLabels.serviceQualityFlags?.[type] || type}
            />
          );
          return acc;
        }, {} as RecordOptions) as OptionsReturnType<V>;
      }

      return options.map((type) => ({
        id: type,
        label: dataLabels.serviceQualityFlags?.[type] || type,
      })) as OptionsReturnType<V>;
    },
    [chipVariants, dataLabels]
  );

  const taskStatusOptions = useCallback(
    <V extends Variant>(_: V): RecordOptions => {
      return {
        [TaskStatus.STATUS_TODO]: (
          <div css={styles.chipLabel}>
            <Icon name='pending-small' css={{ color: statusSwitcherVariantsColor['critical'].color }} />
            <Text>{t('To-Do')}</Text>
          </div>
        ),
        [TaskStatus.STATUS_IN_PROGRESS]: (
          <div css={styles.chipLabel}>
            <Icon name='in-progress-small' css={{ color: statusSwitcherVariantsColor['warning'].color }} />
            <Text>{t('In Progress')}</Text>
          </div>
        ),
        [TaskStatus.STATUS_COMPLETED]: (
          <div css={styles.chipLabel}>
            <Icon name='check-small' css={{ color: statusSwitcherVariantsColor['success'].color }} />
            <Text>{t('Completed')}</Text>
          </div>
        ),
      };
    },
    [t]
  );

  const taskTypeOptions = useCallback(
    <V extends Variant>(variant: V): OptionsReturnType<V> => {
      const options: CallIntelTypes.TaskType[] = [
        CallIntelTypes.TaskType.TYPE_SCHEDULING,
        CallIntelTypes.TaskType.TYPE_WAITLIST,
        CallIntelTypes.TaskType.TYPE_PATIENT_CARE,
        CallIntelTypes.TaskType.TYPE_BILLING,
        CallIntelTypes.TaskType.TYPE_INSURANCE,
        CallIntelTypes.TaskType.TYPE_OTHER,
      ];

      if (variant === 'groupedArray') {
        return [
          {
            label: t('Follow-up Reasons'),
            value: 'follow-up-reason',
            hideLabel: true,
            children: options.map((type) => ({
              label: dataLabels.taskTypes?.[type] || type,
              value: type,
            })),
          },
        ] as OptionsReturnType<V>;
      }

      if (variant === 'record') {
        return options.reduce((acc, type) => {
          acc[type] = (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              css={styles.chip}
              dataKey={type}
              label={dataLabels.taskTypes?.[type] || type}
            />
          );
          return acc;
        }, {} as RecordOptions) as OptionsReturnType<V>;
      }

      return options.map((type) => ({
        id: type,
        label: dataLabels.taskTypes?.[type] || type,
      })) as OptionsReturnType<V>;
    },
    [chipVariants, dataLabels, t]
  );

  const optionProviders = useMemo(
    () => ({
      aiCallOutcome: aiCallOutcomeGroupedOptions,
      appointmentTypes: appointmentTypesOptions,
      callDirection: () => callDirectionOptions,
      categories: categoryOptions,
      contactType: () => contactTypeOptions,
      feedbackIssueTypes: feedbackIssueTypesOptions,
      officeHours: officeHoursOptions,
      schedulingOutcome: schedulingOutcomesOptions,
      schedulingOutcomeV2: schedulingOutcomeV2Options,
      schedulingOpportunity: schedulingOpportunityOptions,
      sentiment: sentimentOptions,
      serviceQualityFlag: serviceQualityFlagOptions,
      taskStatus: taskStatusOptions,
      taskType: taskTypeOptions,
    }),
    [
      aiCallOutcomeGroupedOptions,
      appointmentTypesOptions,
      callDirectionOptions,
      categoryOptions,
      contactTypeOptions,
      feedbackIssueTypesOptions,
      officeHoursOptions,
      schedulingOutcomesOptions,
      schedulingOutcomeV2Options,
      schedulingOpportunityOptions,
      sentimentOptions,
      serviceQualityFlagOptions,
      taskStatusOptions,
      taskTypeOptions,
    ]
  );

  const getOptions = useCallback(
    <T extends OptionType, V extends Variant = 'record'>(
      type: T,
      variant: V = 'record' as V,
      additionalProps?: Record<string, any>
    ): OptionsReturnType<V> => {
      const resolvedVariant = variant;
      const provider = optionProviders[type];
      if (!provider) {
        throw new Error(`Unsupported option type: ${type}`);
      }
      // Special case: aiCallOutcome only supports groupedArray
      if (type === 'aiCallOutcome' && resolvedVariant !== 'groupedArray') {
        throw new Error(`aiCallOutcome only supports 'groupedArray' variant`);
      }
      // Static providers that return RecordOptions directly (no variant parameter)
      if (type === 'callDirection' || type === 'contactType') {
        return (provider as () => RecordOptions)() as OptionsReturnType<V>;
      }
      // Dynamic providers that accept variant and additionalProps
      return provider(resolvedVariant, additionalProps) as OptionsReturnType<V>;
    },
    [optionProviders]
  );

  return { getOptions };
};

const styles = {
  chip: css`
    max-width: none;
  `,
  chipLabel: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(0.5)};
  `,

  schedulingContentWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    align-items: flex-start;
  `,
  unscheduledChipWrapper: css`
    display: flex;
    align-items: center;
    width: 100%;
  `,
  aiIcon: css`
    > span {
      display: flex;
      align-items: center;
    }
  `,
};
